import _isEmpty from 'lodash/isEmpty';

import { navigate } from '@reach/router';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { useCookies } from 'react-cookie';

import { CommonLayoutV2, CustomButtonV2, CustomInputV2, CustomPasswordMeterV2, CustomSnackbarV2 } from 'components/v2';

import { axios } from 'services/axios';
import { COMMON_ERROR_MESSAGE, errorMessageMap, ERRORS_CODE, NETWORK_ERROR_MESSAGE } from 'services/Constant';
import { logService } from 'services/LogService';
import QueryParametersParser from 'services/QueryParametersParser';
import Validator from 'services/Validator';

import './change-password.scss';

const CHANGE_PASSWORD_FIELDS = [
    {
        label: 'Old Password',
        name: 'oldPassword',
        type: 'password',
        isRequired: true,
        validation: (value) => {
            const errors = [];
            if (!value) {
                errors.push('Must be not empty');
            }

            return errors;
        }
    },
    {
        label: 'New Password',
        name: 'password',
        type: 'password',
        isRequired: true,
        validation: (value, oldPassword) => {
            const errors = Validator.checkPassword(value);
            if (oldPassword && value === oldPassword) {
                errors.push('The new password must be different from the current password');
            }
            return errors;
        }
    },
    {
        label: 'Confirm Password',
        name: 'passwordConfirmation',
        type: 'password',
        isRequired: true,
        validation: (value, passwordValue) => {
            const errors = [];
            if (value !== passwordValue) {
                errors.push('Passwords do not match.');
            }

            return errors;
        }
    }
];

const pageTitle = 'OPSWAT Accounts | Change Password';

const ChangePasswordPageV2 = ({ location }) => {
    const [fieldData, setFieldData] = useState({
        oldPassword: '',
        password: '',
        passwordConfirmation: ''
    });
    const [fieldErrors, setFieldErrors] = useState({
        oldPassword: [],
        password: [],
        passwordConfirmation: []
    });
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState([]);
    const [successEmail, setSuccessEmail] = useState(undefined);

    const [cookies] = useCookies(['__opswat-login']);

    const query = useMemo(() => {
        if (location?.state?.email) {
            return { email: location.state.email };
        } else if (location?.search) {
            return QueryParametersParser.parse(location?.search);
        } else if (cookies?.['__opswat-login']) {
            return { email: cookies['__opswat-login'] };
        }
        return undefined;
    }, [location, cookies]);

    const isExternalIdpUser = useMemo(() => {
        if (location?.state?.isExternalIdpUser) {
            return isExternalIdpUser;
        }
        return false;
    }, [location]);

    const onChange = (evt) => {
        const value = evt.target.value;
        const currentInput = CHANGE_PASSWORD_FIELDS.filter((field) => field.name === evt.target.name)[0];
        const passwordInput = CHANGE_PASSWORD_FIELDS.filter((field) => field.name === 'password')[0];
        const passwordConfirmationInput = CHANGE_PASSWORD_FIELDS.filter(
            (field) => field.name === 'passwordConfirmation'
        )[0];
        const isPasswordConfirmation = evt.target.name === 'passwordConfirmation';
        /* eslint-disable indent */
        const currentInputErrors = !isPasswordConfirmation
            ? currentInput?.validation(value, fieldData['oldPassword'])
            : passwordConfirmationInput?.validation(value, fieldData['password']);
        const passwordErrors =
            evt.target.name === 'oldPassword' && fieldData['password'].length
                ? passwordInput?.validation(fieldData['password'], value)
                : evt.target.name === 'password'
                  ? currentInputErrors
                  : fieldErrors['password'];
        const passwordConfirmationErrors =
            evt.target.name === 'password' && fieldData['passwordConfirmation'].length
                ? passwordConfirmationInput?.validation(fieldData['passwordConfirmation'], value)
                : isPasswordConfirmation
                  ? currentInputErrors
                  : fieldErrors['passwordConfirmation'];
        /* eslint-disable indent */

        if (evt.target.type !== 'checkbox') {
            setFieldErrors({
                ...fieldErrors,
                [evt.target.name]: currentInputErrors,
                password: passwordErrors,
                passwordConfirmation: passwordConfirmationErrors
            });
        }

        if (evt.key === 'Enter' && currentInputErrors.length === 0 && passwordConfirmationErrors.length === 0) {
            onSubmit();
        }

        setFieldData({ ...fieldData, [evt.target.name]: value });
    };

    const isValid = useMemo(() => {
        const isError = Object.entries(fieldErrors).filter((error) => error[1]?.length !== 0);
        if (!isError.length) {
            const { oldPassword, password, passwordConfirmation } = fieldData;
            return password && passwordConfirmation && (query?.code || oldPassword);
        }

        return false;
    }, [fieldData, fieldErrors, query]);

    const onSubmit = (e) => {
        e && e.preventDefault();
        if (!isValid || !query?.email) {
            return;
        }

        setIsLoading(true);
        setErrors([]);
        (async () => {
            try {
                let request;
                if (!query.code) {
                    // If change user by current login email
                    request = await axios.post('/change-password', {
                        password: fieldData['password'],
                        oldPassword: fieldData['oldPassword']
                    });
                } else {
                    // If user change password by using reset code
                    request = await axios.post('/forgot-password-submit', {
                        email: query.email,
                        code: query.code,
                        password: fieldData['password']
                    });
                }
                if (request.status === 200) {
                    // Reset cookies after user reset password to require user login again
                    await axios.get('/logout');
                    setSuccessEmail(request.data?.email || true);
                }
            } catch (err) {
                logService.error(err);
                let title;
                const messages = [];
                if (
                    (err?.response?.status >= 400 && err?.response?.status < 500) ||
                    errorMessageMap(err?.response?.data?.error?.code)
                ) {
                    title = 'Error';
                    messages.push(COMMON_ERROR_MESSAGE);
                } else {
                    title = 'Error';
                    messages.push(NETWORK_ERROR_MESSAGE);
                }

                if (err?.response?.data?.error?.code === ERRORS_CODE.notAuthorizedException.code) {
                    messages.push('Your old password is not correct.');
                } else {
                    messages.push(errorMessageMap(err?.response?.data?.error?.code));
                }

                setErrors([
                    {
                        title,
                        error: err,
                        messages
                    }
                ]);
            }
            setIsLoading(false);
        })();
    };

    const changePasswordForm = useMemo(() => {
        return (
            <div className='change-password__form'>
                <h3>Reset Password</h3>
                <div>
                    {!query?.code && (
                        <CustomInputV2
                            required
                            id='oldPassword'
                            name='oldPassword'
                            type='password'
                            label='Old Password'
                            placeholder='Enter password'
                            disabled={isExternalIdpUser}
                            onChange={onChange}
                            value={fieldData.oldPassword}
                            errors={fieldErrors.oldPassword}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && isValid) {
                                    onSubmit(e);
                                }
                            }}
                            highlightErrors={!_isEmpty(errors)}
                        />
                    )}
                    <CustomInputV2
                        required
                        id='password'
                        name='password'
                        type='password'
                        label='New Password'
                        placeholder='Enter password'
                        onChange={onChange}
                        disabled={isExternalIdpUser}
                        value={fieldData.password}
                        errors={fieldErrors.password}
                        rightLabel={<CustomPasswordMeterV2 value={fieldData.password} />}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && isValid) {
                                onSubmit(e);
                            }
                        }}
                        highlightErrors={!_isEmpty(errors)}
                    />
                    <CustomInputV2
                        required
                        id='passwordConfirmation'
                        name='passwordConfirmation'
                        type='password'
                        label='Confirm Password'
                        placeholder='Confirm password'
                        disabled={isExternalIdpUser}
                        onChange={onChange}
                        value={fieldData.passwordConfirmation}
                        errors={fieldErrors.passwordConfirmation}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && isValid) {
                                onSubmit(e);
                            }
                        }}
                        highlightErrors={!_isEmpty(errors)}
                    />
                </div>
                {!_isEmpty(errors) && <CustomSnackbarV2 messageList={errors} />}
            </div>
        );
    }, [fieldData, fieldErrors, query, errors]);

    useEffect(() => {
        if (successEmail) {
            // Remove email from query but preserve the extra query attributes to send them with QueryParams
            // eslint-disable-next-line no-unused-vars
            const { email, ...extraQueryFields } = query;
            if (Object.keys(extraQueryFields).length) {
                navigate('/login' + QueryParametersParser.toString(extraQueryFields), {
                    state: { email: typeof successEmail === 'string' ? successEmail : undefined }
                });
            } else {
                navigate('/login', { state: { email: typeof successEmail === 'string' ? successEmail : undefined } });
            }
        }
    }, [successEmail, query]);

    useEffect(() => {
        if (!query?.email) {
            navigate('/login' + QueryParametersParser.toString(query));
        }
    }, [query]);

    return (
        <CommonLayoutV2 isLoading={false} pageTitle={pageTitle}>
            <div className='change-password__container'>{changePasswordForm}</div>
            <CustomButtonV2
                onClick={onSubmit}
                disabled={!isValid || isLoading || isExternalIdpUser}
                loading={isLoading}
            >
                Reset Password
            </CustomButtonV2>
        </CommonLayoutV2>
    );
};

export default ChangePasswordPageV2;

ChangePasswordPageV2.propTypes = {
    location: PropTypes.object
};
